import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { SortingState } from '@tanstack/react-table';

import { Table } from '@/common';
import { Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query.js';
import { PAGE_SIZE } from '@/constants';
import { AuthContext, OfflineContext, RolesAndPermissionContext } from '@/contexts';
import { PERMISSION_TYPE } from '@/enums';
import { getEventsColumns } from '@/helpers';
import { formatDate } from '@/helpers/date';
import { UploadContentLayout } from '@/layout';
import { Filters } from '@/pages/Events/Filters';
import { useDeleteEventMutation, useGetAllEvents } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { useFilterStore } from '@/store';
import { AllowedPermissions, Event, SelectOptionProps, TableData } from '@/types';

export const EVENTS_PATH = '/events/';
export const EVENTS_REFETCH_INTERVAL = 10000;

export const Events = () => {
  const { permissions } = useContext(AuthContext);
  const { isAllowedTo } = useContext(RolesAndPermissionContext);
  const { isOnline, offlineEvents } = useContext(OfflineContext);

  const allowedPermissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_EVENT,
    editPermission: PERMISSION_TYPE.UPDATE_EVENT,
    deletePermission: PERMISSION_TYPE.DELETE_EVENT,
    viewPermission: PERMISSION_TYPE.VIEW_EVENTS
  };

  const [t] = useTranslation('common');
  const [search, setSearch] = useState<string>();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: false }]);
  const { currentUserData: currentUser } = useContext(AuthContext);
  const { organizationFilter } = useFilterStore();
  const [appliedFiltersCounter, setAppliedFiltersCounter] = useState<number>(0);

  const [filters, setFilters] = useState<{
    status: SingleValue<SelectOptionProps>;
    organization: SingleValue<SelectOptionProps>;
  }>({
    status: null,
    organization: null
  });

  const getOrganizationValue = () => {
    if (organizationFilter) {
      return organizationFilter.value as number;
    }

    if (filters.organization === null && currentUser?.organizations.length === 1) {
      return currentUser?.organizations[0].id as number;
    }

    return filters.organization?.value as number;
  };

  const [selectedEvent, setSelectedEvent] = useState<Event | undefined>();
  const navigate = useNavigate();

  const {
    isLoading,
    data: allEvents,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch: refetchAllEvents
  } = useGetAllEvents(
    PAGE_SIZE,
    search,
    sorting.map((s) => s.id.charAt(0).toUpperCase() + s.id.slice(1)).toString(),
    sorting.some((s) => s.desc),
    filters.status?.value as boolean,
    getOrganizationValue() ? [getOrganizationValue()] : undefined
  );

  const { mutate: deleteEvent } = useDeleteEventMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleDelete = (eventData: Event) => {
    setSelectedEvent(eventData);
    setIsOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedEvent(undefined);
  };

  const handleCloseFiltersModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleViewDetails = (row: Event) => {
    if (isAllowedTo([PERMISSION_TYPE.ORG_ACCESS])) {
      navigate(EVENTS_PATH + row.id);
    } else {
      navigate(EVENTS_PATH + row.id + '/upload-content/requests');
    }
  };

  const formatData = (): TableData[] => {
    return allEvents?.pages.flatMap((page) =>
      (page.data?.items ?? []).map((eventData: Event) => ({
        ...eventData,
        date: formatDate(new Date(eventData.startDateAndTime)),
        organization: eventData.organization?.name
      }))
    ) as TableData[];
  };

  const handleCreate = () => {
    navigate('/events/create');
  };

  const handleEdit = (eventData: TableData) => {
    navigate(`/events/${eventData.id}/edit`);
  };

  const resetFilters = () => {
    setIsFilterModalOpen(false);
    setFilters({ status: null, organization: null });
    setAppliedFiltersCounter(0);
  };

  const applyFilters = (values: Record<string, SelectOptionProps>) => {
    setIsFilterModalOpen(false);
    setFilters({
      status: values.filterByStatus as SelectOptionProps,
      organization: values.filterByOrganization as SelectOptionProps
    });
  };

  useEffect(() => {
    const numberOfFilters = Object.values(filters).filter((value) => value !== null);
    setAppliedFiltersCounter(numberOfFilters.length);
  }, [filters]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      !document.hidden && refetchAllEvents();
    }, EVENTS_REFETCH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [refetchAllEvents]);

  //Offline mode: show only ceremonies that have downloaded songs
  if (!isOnline) {
    const offlineRows = offlineEvents.map((e) => ({
      id: e.id,
      name: e.name,
      description: e.description ?? '',
      startDateAndTime: e.startDateAndTime ? new Date(e.startDateAndTime) : undefined,
      date: e.startDateAndTime ? formatDate(new Date(e.startDateAndTime)) : '',
      organization: e.organizationName ?? '',
      status: e.status ?? '',
      enabled: true
    }));

    return (
      <div className='flex size-full flex-col items-center'>
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          <Table
            data={offlineRows as unknown as TableData[]}
            columns={getEventsColumns(false)}
            tableTitle={t('events')}
            permissions={{
              createPermission: '' as never,
              editPermission: '' as never,
              deletePermission: '' as never,
              viewPermission: '' as never
            }}
            handleViewDetails={(item) => handleViewDetails(item as Event)}
            sorting={sorting}
            setSorting={setSorting}
            hiddenColumnsMobile={['enabled', 'status']}
            fetchNextPage={() => undefined}
            hasNextPage={false}
            isFetchingNextPage={false}
            numberOfAppliedFilters={0}
          />
        </div>
      </div>
    );
  }

  if (
    isAllowedTo([PERMISSION_TYPE.EVENT_ACCESS]) &&
    !isAllowedTo([PERMISSION_TYPE.ORG_ACCESS]) &&
    !currentUser?.events?.length
  ) {
    return (
      <UploadContentLayout>
        <div
          data-testid={TestIds.events.noCeremoniesAssigned}
          className='mt-10 flex max-w-md flex-col items-center gap-2 text-center'>
          <Label text={t('noCeremoniesAssigned')} type={LABEL_TYPE.H3} />
          <Label text={t('noCeremoniesAssignedDescription')} type={LABEL_TYPE.PARAGRAPH1} />
        </div>
      </UploadContentLayout>
    );
  }

  return (
    <div className='flex size-full flex-col items-center'>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          {allEvents && (
            <Table
              data={formatData()}
              columns={getEventsColumns(permissions.includes(PERMISSION_TYPE.VIEW_ORGANIZATIONS))}
              testIdPrefix={TestIds.events.prefix}
              tableTitle={t('events')}
              addButtonLabel={t('addEvent')}
              permissions={allowedPermissions}
              search={searchCallback}
              handleCreate={handleCreate}
              handleEdit={handleEdit}
              handleDelete={(item) => handleDelete(item as Event)}
              handleOpenFilterModal={() => setIsFilterModalOpen(!isFilterModalOpen)}
              handleViewDetails={(item) => handleViewDetails(item as Event)}
              sorting={sorting}
              setSorting={setSorting}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              numberOfAppliedFilters={appliedFiltersCounter}
              hiddenColumnsMobile={['enabled', 'status']}
            />
          )}
        </div>
      )}
      {selectedEvent && (
        <ConfirmationModal
          isOpen={isOpenDeleteModal}
          message={t('deleteMessage', { item: selectedEvent.name })}
          onClose={handleCloseDeleteModal}
          onConfirm={() => deleteEvent(selectedEvent?.id)}
        />
      )}
      {isFilterModalOpen && (
        <Filters
          apply={applyFilters}
          reset={resetFilters}
          onClose={handleCloseFiltersModal}
          filterByStatusValue={filters.status}
          filterByOrganization={filters.organization}
        />
      )}
    </div>
  );
};
